import { useState, useEffect, useCallback, useRef } from "react";

const sumAmounts = (records) =>
  (records ?? []).reduce((acc, r) => acc + (r.amount ?? 0), 0);

const sumAdjustmentAmounts = (records) =>
  (records ?? []).reduce(
    (acc, r) => acc + (r.adjustmentAmount ?? r.adjustedAmount ?? 0),
    0,
  );

const sumRecordAdjustments = (records) =>
  (records ?? []).reduce(
    (acc, r) => acc + sumAdjustmentAmounts(r.adjustments),
    0,
  );

const normalizeStatus = (status) =>
  typeof status === "string" ? status.trim().toLowerCase() : status;

const isRejectedStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "rejected" || normalized === "declined";
};

const normalizeContractAdjustment = (adjustment) => ({
  ...adjustment,
  adjustmentAmount: adjustment.adjustmentAmount ?? 0,
});

const normalizeBonusAdjustment = (adjustment) => ({
  ...adjustment,
  adjustmentAmount:
    adjustment.adjustmentAmount ?? adjustment.adjustedAmount ?? 0,
});

const normalizeBonusRecord = (record) => {
  const sourceBonusRecord =
    record?.bonusRecord && typeof record.bonusRecord === "object"
      ? record.bonusRecord
      : {};

  return {
    ...sourceBonusRecord,
    ...record,
    bonusRecordId:
      record?.bonusRecordId ??
      sourceBonusRecord?.bonusRecordId ??
      record?.id ??
      null,
    amount:
      record?.amount ??
      sourceBonusRecord?.amount ??
      record?.approvedAmount ??
      0,
    status: normalizeStatus(record?.status ?? sourceBonusRecord?.status),
    adjustments: (
      record?.adjustments ??
      sourceBonusRecord?.adjustments ??
      []
    ).map(normalizeBonusAdjustment),
  };
};

const normalizeChargebackRecord = (record) => {
  const sourceChargebackRecord =
    record?.chargebackRecord && typeof record.chargebackRecord === "object"
      ? record.chargebackRecord
      : {};

  return {
    ...sourceChargebackRecord,
    ...record,
    chargebackRecordId:
      record?.chargebackRecordId ??
      sourceChargebackRecord?.chargebackRecordId ??
      record?.id ??
      null,
    amount:
      record?.amount ??
      record?.approvedAmount ??
      sourceChargebackRecord?.amount ??
      0,
    status: normalizeStatus(record?.status ?? sourceChargebackRecord?.status),
    adjustments: (
      record?.adjustments ??
      sourceChargebackRecord?.adjustments ??
      []
    ).map(normalizeBonusAdjustment),
  };
};

const normalizeReport = (report) => {
  const files = (report?.files ?? report?.Files ?? []).map((file) => ({
    ...file,
    id: file?.commissionReportFileId ?? file?.fileName,
    name: file?.fileName ?? "Report file",
    createdAt: file?.requestDate ?? null,
  }));

  return {
    ...report,
    referenceId: report?.referenceId ?? null,
    reportName: report?.friendlyName ?? "Report",
    archiveFileName: report?.archiveFileName ?? null,
    status: report?.status ?? null,
    createdAt: report?.requestDate ?? null,
    files,
  };
};

const getAdjustmentId = (adjustment) =>
  adjustment?.payoutRunAgentAdjustmentId ??
  adjustment?.agentAdjustmentId ??
  adjustment?.id ??
  null;

const toAdjustmentPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (Array.isArray(payload)) return payload[0] ?? null;
  if (payload.agentAdjustment && typeof payload.agentAdjustment === "object") {
    return payload.agentAdjustment;
  }
  if (payload.data && typeof payload.data === "object") return payload.data;
  return payload;
};

const normalizeAgentContract = (ac) => ({
  ...ac,
  status: normalizeStatus(ac.status),
  contractAdjustments: (ac.contractAdjustments ?? []).map(
    normalizeContractAdjustment,
  ),
  commissionRecords: (ac.commissionRecords ?? ac.CommissionRecords ?? []).map(
    (r) => ({
      ...r,
      status: normalizeStatus(r.status),
      itemRecords: r.itemRecords ?? [],
      chargebackRecords: (r.chargebackRecords ?? []).map((c) => ({
        ...c,
        status: normalizeStatus(c.status),
      })),
    }),
  ),
});

const mapPayoutRun = (run) => {
  const agentContracts = (run.agentContracts ?? []).map(normalizeAgentContract);
  const bonusRecords = (run.bonusRecords ?? []).map(normalizeBonusRecord);
  const chargebackRecords = (run.chargebackRecords ?? []).map(
    normalizeChargebackRecord,
  );
  const agentAdjustments = run.agentAdjustments ?? [];
  const reports = (run.reports ?? run.generatedReports ?? []).map(
    normalizeReport,
  );
  const latestReport = run.latestReport
    ? normalizeReport(run.latestReport)
    : reports.reduce((latest, report) => {
        if (!latest) return report;
        return new Date(report.createdAt || 0) > new Date(latest.createdAt || 0)
          ? report
          : latest;
      }, null);

  const commissions = agentContracts.reduce(
    (acc, ac) => acc + sumAmounts(ac.commissionRecords),
    0,
  );
  const chargebacks = sumAmounts(chargebackRecords);
  const adjustments =
    agentContracts.reduce(
      (acc, ac) => acc + sumAdjustmentAmounts(ac.contractAdjustments),
      0,
    ) +
    sumRecordAdjustments(bonusRecords) +
    sumRecordAdjustments(chargebackRecords) +
    sumAdjustmentAmounts(agentAdjustments);
  const bonuses = sumAmounts(bonusRecords);
  const rejectedCommissions = agentContracts.reduce(
    (acc, ac) =>
      isRejectedStatus(ac.status)
        ? acc + sumAmounts(ac.commissionRecords)
        : acc,
    0,
  );
  const rejectedBonuses = bonusRecords.reduce(
    (acc, b) => (isRejectedStatus(b.status) ? acc + (b.amount ?? 0) : acc),
    0,
  );
  const rejectedChargebacks = chargebackRecords.reduce(
    (acc, c) => (isRejectedStatus(c.status) ? acc + (c.amount ?? 0) : acc),
    0,
  );
  const rejected = rejectedCommissions + rejectedBonuses + rejectedChargebacks;
  const net = commissions + bonuses + chargebacks + adjustments;
  const netPayable = net - rejected;

  return {
    payoutRunId: run.payoutRunId,
    customerId: run.customerId,
    customerCode: run.customerCode,
    name: run.name,
    periodStart: run.periodStart,
    periodEnd: run.periodEnd,
    status: normalizeStatus(run.status),
    reviewedBy: run.reviewedBy,
    lastUpdated: run.lastUpdated,
    notes: run.notes,
    agentContracts,
    bonusRecords,
    chargebackRecords,
    agentAdjustments,
    reports,
    latestReport,
    commissions,
    bonuses,
    chargebacks,
    adjustments,
    rejected,
    net,
    netPayable,
  };
};

const remapRunWithAdjustments = (run, agentAdjustments) =>
  mapPayoutRun({
    ...run,
    agentAdjustments,
  });

export function usePayoutRun({ api, payoutRunId, onError }) {
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [isLoadingReportTypes, setIsLoadingReportTypes] = useState(false);
  const apiRef = useRef(api);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const fetchRun = useCallback(async (id, { showLoader = true } = {}) => {
    if (!id) return;
    if (showLoader) setIsLoading(true);
    try {
      const response = await apiRef.current.get(
        `/api/commission/payout-runs/${id}`,
        { suppressGlobalError: true },
      );
      if (response.status === 200) {
        setRun(mapPayoutRun(response.data));
      } else {
        setRun(null);
      }
    } catch {
      setRun(null);
      if (onErrorRef.current) onErrorRef.current();
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, []);

  const saveAgentAdjustment = useCallback(
    async ({
      payoutRunId: id,
      mode,
      adjustmentId,
      agentId,
      agentName,
      amount,
      reason,
    }) => {
      if (!id) throw new Error("Missing payout run id.");

      if (mode === "edit") {
        if (!adjustmentId) throw new Error("Missing adjustment id.");
        const response = await apiRef.current.put(
          `/api/commission/payout-runs/${id}/agent-adjustments/${adjustmentId}`,
          {
            adjustmentAmount: amount,
            reason,
          },
          { suppressGlobalError: true },
        );

        await fetchRun(id, { showLoader: false });

        return response?.data;
      }

      const response = await apiRef.current.post(
        `/api/commission/payout-runs/${id}/agent-adjustments`,
        {
          agentId,
          adjustmentAmount: amount,
          reason,
        },
        { suppressGlobalError: true },
      );

      const serverCreated = toAdjustmentPayload(response?.data);

      setRun((prev) => {
        if (!prev) return prev;

        const nextAdjustment = serverCreated ?? {
          agentId,
          agentName,
          adjustmentAmount: amount,
          reason,
        };

        const nextAdjustments = [
          ...(prev.agentAdjustments ?? []),
          {
            ...nextAdjustment,
            adjustmentAmount: nextAdjustment.adjustmentAmount ?? amount,
            reason: nextAdjustment.reason ?? reason,
          },
        ];

        return remapRunWithAdjustments(prev, nextAdjustments);
      });

      // Some create endpoints return no canonical id. Pull fresh state silently so
      // delete/edit can work immediately against server-backed identifiers.
      if (getAdjustmentId(serverCreated) == null) {
        await fetchRun(id, { showLoader: false });
      }

      return response?.data;
    },
    [fetchRun],
  );

  const deleteAgentAdjustment = useCallback(
    async ({ payoutRunId: id, adjustmentId }) => {
      if (!id) throw new Error("Missing payout run id.");
      if (!adjustmentId) throw new Error("Missing adjustment id.");

      await apiRef.current.delete(
        `/api/commission/payout-runs/${id}/agent-adjustments/${adjustmentId}`,
        { suppressGlobalError: true },
      );

      setRun((prev) => {
        if (!prev) return prev;

        const nextAdjustments = (prev.agentAdjustments ?? []).filter(
          (a) => String(getAdjustmentId(a)) !== String(adjustmentId),
        );

        return remapRunWithAdjustments(prev, nextAdjustments);
      });
    },
    [],
  );

  const updateRecordStatus = useCallback(
    async ({ target, status, payoutRunId: targetPayoutRunId, notes }) => {
      if (!target) throw new Error("Missing target.");

      const normalizedStatus = (status ?? "").toLowerCase();
      const payloadStatus =
        normalizedStatus === "approved"
          ? "Approved"
          : normalizedStatus === "rejected" || normalizedStatus === "declined"
            ? "Declined"
            : "Pending";
      const payloadNotes = notes?.trim() || null;

      if (target.kind === "commission") {
        if (!target.contractNumber) {
          throw new Error("Missing contract number for commission record.");
        }

        await apiRef.current.put(
          `/api/commission/agent-contract/${target.id}/status`,
          {
            payoutRunId: targetPayoutRunId,
            status: payloadStatus,
            notes: payloadNotes,
          },
          { suppressGlobalError: true },
        );
        return;
      }

      if (target.kind === "bonus") {
        if (!target.id) {
          throw new Error("Missing bonus record id.");
        }

        await apiRef.current.put(
          `/api/commission/bonus-record/${target.id}/status`,
          {
            status: payloadStatus,
            payoutRunId: targetPayoutRunId,
            notes: payloadNotes,
          },
          { suppressGlobalError: true },
        );
        return;
      }

      if (target.kind === "chargeback") {
        if (!target.id) throw new Error("Missing chargeback record id.");

        await apiRef.current.put(
          `/api/commission/chargeback-record/${target.id}/status`,
          {
            status: payloadStatus,
            payoutRunId: targetPayoutRunId,
            notes: payloadNotes,
          },
          { suppressGlobalError: true },
        );
        return;
      }

      throw new Error("Unsupported target kind.");
    },
    [],
  );

  const sendRecordBackToPool = useCallback(async ({ target, payoutRunId }) => {
    if (!target) throw new Error("Missing target.");

    if (target.kind === "commission") {
      if (!target.id) {
        throw new Error("Missing id for agent commission record.");
      }

      await apiRef.current.put(
        `/api/commission/agent-contract/${target.id}/back-to-pool`,
        {
          payoutRunId: payoutRunId,
        },
        { suppressGlobalError: true },
      );
      return;
    }

    if (target.kind === "bonus") {
      if (!target.id) {
        throw new Error("Missing bonus record id.");
      }

      await apiRef.current.put(
        `/api/commission/bonus-record/${target.id}/back-to-pool`,
        {
          status: "Pending",
          payoutRunId: payoutRunId,
        },
        { suppressGlobalError: true },
      );
      return;
    }

    if (target.kind === "chargeback") {
      if (!target.id) throw new Error("Missing chargeback record id.");

      await apiRef.current.put(
        `/api/commission/chargeback-record/${target.id}/back-to-pool`,
        { payoutRunId, chargebackRecordId: target.id, status: "Pending" },
        { suppressGlobalError: true },
      );
      return;
    }

    throw new Error("Unsupported target kind.");
  }, []);

  const addRecordAdjustment = useCallback(
    async ({ target, amount, reason, payoutRunId: targetPayoutRunId }) => {
      if (!target) throw new Error("Missing target.");

      if (target.kind === "commission") {
        if (!target.id) {
          throw new Error("Missing id for agent contract.");
        }

        await apiRef.current.post(
          `/api/commission/agent-contract/${target.id}/adjustments`,
          {
            commissionAgentContractId: target.id,
            adjustmentAmount: amount,
            reason,
            payoutRunId: targetPayoutRunId,
          },
          { suppressGlobalError: true },
        );
        return;
      }

      if (target.kind === "bonus") {
        if (!target.id) {
          throw new Error("Missing bonus record id.");
        }

        await apiRef.current.post(
          `/api/commission/bonus-record/${target.id}/adjustments`,
          {
            adjustmentAmount: amount,
            reason,
            payoutRunId: targetPayoutRunId,
          },
          { suppressGlobalError: true },
        );
        return;
      }

      if (target.kind === "chargeback") {
        if (!target.id) throw new Error("Missing chargeback record id.");

        await apiRef.current.post(
          `/api/commission/chargeback-record/${target.id}/adjustments`,
          { adjustmentAmount: amount, reason, payoutRunId: targetPayoutRunId },
          { suppressGlobalError: true },
        );
        return;
      }

      throw new Error("Unsupported target kind.");
    },
    [],
  );

  const submitRunDecision = useCallback(
    async ({ id, action, notes, removeContractsToPoolOnDecline = true }) => {
      if (!id) throw new Error("Missing payout run id.");
      if (!action) throw new Error("Missing payout run action.");

      await apiRef.current.post(
        `/api/commission/payout-runs/${id}/${action}`,
        {
          notes: notes?.trim() || null,
          removeContractsToPoolOnDecline,
        },
        { suppressGlobalError: true },
      );
    },
    [],
  );

  const addAgentContractToPayoutRun = useCallback(
    async ({ payoutRunId: id, agentContractId, agentContractIds }) => {
      if (!id) throw new Error("Missing payout run id.");
      const ids =
        agentContractIds ?? (agentContractId ? [agentContractId] : []);
      if (ids.length === 0) throw new Error("Missing agent contract id.");

      await Promise.all(
        ids.map((agentContractId) =>
          apiRef.current.put(
            `/api/commission/agent-contract/${agentContractId}/payout-run`,
            {
              payoutRunId: id,
              commissionAgentContractId: agentContractId,
            },
            { suppressGlobalError: true },
          ),
        ),
      );
    },
    [],
  );

  const addChargebackToPayoutRun = useCallback(
    async ({ payoutRunId: id, chargebackRecordId, chargebackRecordIds }) => {
      if (!id) throw new Error("Missing payout run id.");
      const ids =
        chargebackRecordIds ?? (chargebackRecordId ? [chargebackRecordId] : []);
      if (ids.length === 0) throw new Error("Missing chargeback record id.");

      await Promise.all(
        ids.map((chargebackRecordId) =>
          apiRef.current.put(
            `/api/commission/chargeback-record/${chargebackRecordId}/payout-run`,
            { payoutRunId: id, chargebackRecordId },
            { suppressGlobalError: true },
          ),
        ),
      );
    },
    [],
  );

  const requestCommissionReport = useCallback(
    async ({
      payoutRunId: id,
      reportId,
      outputFormat,
      agentId = null,
      CreateSeparateFiles = false,
      parameters = {},
    }) => {
      if (!id) throw new Error("Missing payout run id.");
      if (!reportId) throw new Error("Missing report id.");
      if (!outputFormat) throw new Error("Missing output format.");

      return await apiRef.current.post(
        "/api/v1/commission-report",
        {
          payoutRunId: id,
          agentId,
          reportId,
          outputFormat,
          CreateSeparateFiles,
          parameters,
        },
        { suppressGlobalError: true },
      );
    },
    [],
  );

  const downloadCommissionReport = useCallback(
    async ({ payoutRunId: id, referenceId }) => {
      if (!id) throw new Error("Missing payout run id.");
      if (!referenceId) throw new Error("Missing report reference id.");

      return await apiRef.current.getBlob(
        `/api/commission/payout-runs/${id}/report/${encodeURIComponent(referenceId)}`,
      );
    },
    [],
  );

  const fetchCommissionReportTypes = useCallback(async () => {
    setIsLoadingReportTypes(true);
    try {
      const response = await apiRef.current.get(
        "/api/v1/commission-report/types",
        { suppressGlobalError: true },
      );

      const data = Array.isArray(response?.data) ? response.data : [];
      const nextTypes = data
        .map((item) => ({
          reportId: item?.reportId ?? item?.ReportId,
          friendlyName: item?.friendlyName ?? item?.FriendlyName,
        }))
        .filter((item) => item.reportId != null && item.friendlyName);

      setReportTypes(nextTypes);
      return nextTypes;
    } catch {
      setReportTypes([]);
      throw new Error("Could not load commission report types.");
    } finally {
      setIsLoadingReportTypes(false);
    }
  }, []);

  useEffect(() => {
    fetchRun(payoutRunId);
  }, [fetchRun, payoutRunId]);

  return {
    run,
    isLoading,
    reportTypes,
    isLoadingReportTypes,
    fetchRun,
    fetchCommissionReportTypes,
    saveAgentAdjustment,
    deleteAgentAdjustment,
    updateRecordStatus,
    sendRecordBackToPool,
    addRecordAdjustment,
    submitRunDecision,
    addAgentContractToPayoutRun,
    addChargebackToPayoutRun,
    requestCommissionReport,
    downloadCommissionReport,
  };
}
